"use client";

import { useEffect, useState } from "react";
import CyclingImage from "./CyclingImage";

export type Tile = { pos: string; rot: string; from: string; to: string };

type Props = {
  tiles: Tile[];
  images: string[];
  /** Time between single-tile swaps, in ms. */
  interval?: number;
};

/**
 * Floating tile collage whose images rotate over time. The tiles are coordinated
 * by a single timer so that no two tiles ever show the same image at once: each
 * tick advances one tile (round-robin) to the next image that isn't currently
 * displayed anywhere. Requires more images than tiles to rotate; otherwise it
 * just shows a fixed distinct set.
 */
// All rotation state lives in one object so the updater stays pure: `slots` is
// what each tile shows, `turn` is the round-robin cursor, `next` the candidate
// image to try. Keeping these in state (not closure-captured mutables) means a
// double-invoked updater — e.g. React StrictMode in dev — produces the same
// result instead of advancing the cursors twice and skipping tiles.
type Rotation = { slots: number[]; turn: number; next: number };

export default function CommunityTiles({ tiles, images, interval = 3200 }: Props) {
  const [{ slots }, setRotation] = useState<Rotation>(() => ({
    slots: tiles.map((_, i) => (images.length ? i % images.length : 0)),
    turn: 0,
    next: tiles.length % (images.length || 1),
  }));

  useEffect(() => {
    if (images.length <= tiles.length) return; // can't swap without repeats
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    const id = setInterval(() => {
      setRotation((prev) => {
        const tile = prev.turn % tiles.length;
        // Advance to the next image not currently shown by any tile.
        let candidate = prev.next;
        while (prev.slots.includes(candidate)) {
          candidate = (candidate + 1) % images.length;
        }

        const updated = [...prev.slots];
        updated[tile] = candidate;
        return {
          slots: updated,
          turn: prev.turn + 1,
          next: (candidate + 1) % images.length,
        };
      });
    }, interval);

    return () => clearInterval(id);
  }, [tiles.length, images.length, interval]);

  return (
    <div className="relative ml-auto aspect-[488/496] w-full max-w-[460px]">
      {tiles.map((t, i) => (
        <div
          key={i}
          className={`absolute overflow-hidden rounded-2xl shadow-2xl shadow-black/50 ${t.pos}`}
          style={{
            background: `linear-gradient(140deg, ${t.from}, ${t.to})`,
            transform: `rotate(${t.rot})`,
          }}
        >
          <CyclingImage images={images} active={slots[i]} />
        </div>
      ))}
    </div>
  );
}
